import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { getLoginMethodLabel } from 'src/app/enums/loginMethod.enum';
import { User } from 'src/app/models/user.model';
import { AuthService } from 'src/app/services/auth.service';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from 'src/app/services/translate.service';
import { UserService } from 'src/app/services/user.service';
import { ConfirmDialogComponent, ConfirmDialogData } from 'src/app/shared/confirmDialog/confirmDialog.component';
import { FLYOUT_DATA } from 'src/app/shared/flyout/flyout-container/flyout-data.token';

type AccountSecurityAction = 'disable' | 'enable' | 'unlock';

type UserSecurityFlyoutData = {
    id?: string;
};

interface UserAccountSecurityViewModel {
    avatarText: string;
    fullName: string;
    userName: string;
    roleName: string;
    jobTitle: string;
    lastSuccessfulLogin: string | Date | null;
    loginMethod: string;
    accountStatusLabel: string;
    accountStatusClass: string;
    lockStateLabel: string;
    lockStateClass: string;
    lastStatusChangePrefix: string;
    lastStatusChangedBy: string | null;
    lastStatusChangedAt: string | Date | null;
    showDisableButton: boolean;
    disableButtonDisabled: boolean;
    showEnableButton: boolean;
    enableButtonDisabled: boolean;
    showUnlockButton: boolean;
    lockoutNote: string;
    footerNote: string;
}

@Component({
  standalone: false,
    selector: 'app-user-account-security-popup',
    templateUrl: './user-account-security-popup.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './user-account-security-popup.component.scss',
})
export class UserAccountSecurityPopupComponent implements OnInit, OnDestroy {
    isLoading = false;
    viewModel: UserAccountSecurityViewModel | null = null;

    private userId = '';
    private currentUserStatus: number | boolean | string | null = null;
    private optimisticStatus: number | boolean | string | null = null;
    private optimisticStatusChangedByUserId: string | null = null;
    private optimisticStatusChangedBy: string | null = null;
    private optimisticStatusChangedAt: string | Date | null = null;
    private optimisticIsLocked: boolean | null = null;
    private readonly destroy$ = new Subject<void>();
    private readonly currentUserId: string | undefined;

    constructor(
        @Inject(FLYOUT_DATA) private readonly data: string | UserSecurityFlyoutData,
        private readonly userService: UserService,
        private readonly toastService: ToastService,
        private readonly dialog: MatDialog,
        private readonly dialogRef: MatDialogRef<unknown>,
        private readonly translateService: TranslateService,
        private readonly authService: AuthService,
    ) {
        const currentUser = this.authService.getCurrentUser();
        this.currentUserId = currentUser?.userId;
    }

    ngOnInit(): void {
        this.userId = this.getUserId(this.data);
        if (!this.userId) {
            this.toastService.displayErrorMessage(
                this.translateService.getDynamicTranslation('Unable to load account security details.'),
                true,
            );
            return;
        }

        this.loadAccountSecurity();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    openDisableConfirmDialog(): void {
        this.openActionConfirmDialog('disable');
    }

    openEnableConfirmDialog(): void {
        this.openActionConfirmDialog('enable');
    }

    openUnlockConfirmDialog(): void {
        this.openActionConfirmDialog('unlock');
    }

    closeFlyout(): void {
        if (typeof this.dialogRef.close === 'function') {
            this.dialogRef.close();
        }
    }

    private loadAccountSecurity(): void {
        this.isLoading = true;
        this.userService
            .getAccountSecurityById(this.userId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    const mergedUser = this.applyOptimisticUpdates(response);
                    this.currentUserStatus = mergedUser.status ?? null;
                    this.viewModel = this.createViewModel(mergedUser);
                    this.isLoading = false;
                },
                error: (error) => {
                    this.toastService.showError(error);
                    this.viewModel = null;
                    this.isLoading = false;
                },
            });
    }

    private openActionConfirmDialog(action: AccountSecurityAction): void {
        const actionConfig = this.getActionConfig(action);
        const data: ConfirmDialogData = {
            title: this.translateService.getDynamicTranslation(actionConfig.titleKey),
            message: this.translateService.getDynamicTranslation(actionConfig.messageKey),
            primaryButtonText: this.translateService.getDynamicTranslation('Confirm'),
            primaryCallback: () => actionConfig.callback(this.userId),
            successMessage: actionConfig.successMessageKey,
            secondaryButtonText: this.translateService.getDynamicTranslation('Cancel'),
            errorMessage: actionConfig.errorMessageKey,
        };

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data,
            width: '450px',
            disableClose: true,
        });

        dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe({
            next: (result: unknown) => {
                if (result) {
                    this.applyOptimisticAccountUpdate(action);
                    this.loadAccountSecurity();
                }
            },
        });
    }

    private getActionConfig(action: AccountSecurityAction): {
        titleKey: string;
        messageKey: string;
        successMessageKey: string;
        errorMessageKey: string;
        callback: (userId: string) => ReturnType<UserService['disableUserAccount']>;
    } {
        if (action === 'disable') {
            return {
                titleKey: 'Disable Account',
                messageKey: 'Are you sure you want to disable this account? The user will be signed out and unable to log in.',
                successMessageKey: 'Account disabled successfully',
                errorMessageKey: 'Error disabling account',
                callback: (userId: string) => this.disableUserAccount(userId),
            };
        }

        if (action === 'enable') {
            return {
                titleKey: 'Enable Account',
                messageKey: 'Are you sure you want to enable this account? The user will regain access immediately.',
                successMessageKey: 'Account enabled successfully',
                errorMessageKey: 'Error enabling account',
                callback: (userId: string) => this.enableUserAccount(userId),
            };
        }

        return {
            titleKey: 'Unlock Account',
            messageKey: 'Are you sure you want to unlock this account?',
            successMessageKey: 'Account unlocked successfully',
            errorMessageKey: 'Error unlocking account',
            callback: (userId: string) => this.userService.unlockUserAccount(userId),
        };
    }

    private createViewModel(response: User): UserAccountSecurityViewModel {
        const isDisabled = this.isDisabledAccount(response);
        const isLocked = this.isLockedAccount(response);
        const isCurrentUser = this.currentUserId === this.userId;

        return {
            avatarText: this.getAvatarText(response),
            fullName: response.fullName || '-',
            userName: response.userName || '-',
            roleName: response.roleName || '-',
            jobTitle: response.jobTitle || '-',
            lastSuccessfulLogin: response.lastLoginDate ?? null,
            loginMethod: getLoginMethodLabel(response.lastLoginMethod),
            accountStatusLabel: isDisabled ? 'Disabled' : 'Active',
            accountStatusClass: isDisabled
                ? 'status-badge status-badge--disabled'
                : 'status-badge status-badge--active',
            lockStateLabel: isLocked ? 'Locked' : 'Unlocked',
            lockStateClass: isLocked
                ? 'status-badge status-badge--locked'
                : 'status-badge status-badge--unlocked',
            lastStatusChangePrefix: isDisabled ? 'Disabled by' : 'Enabled by',
            lastStatusChangedBy: response.statusChangedByUserName || null,
            lastStatusChangedAt: response.statusChangedDate ?? null,
            showDisableButton: !isDisabled,
            disableButtonDisabled: isCurrentUser,
            showEnableButton: isDisabled,
            enableButtonDisabled: isCurrentUser,
            showUnlockButton: isLocked,
            lockoutNote: isLocked ? 'Account locked after too many consecutive failed login attempts.' : '',
            footerNote: isCurrentUser
                ? 'You cannot change your own account status.'
                : '',
        };
    }

    private isDisabledAccount(response: User): boolean {
        if (typeof response.status === 'boolean') {
            return !response.status;
        }

        if (typeof response.status === 'number') {
            return response.status !== 0;
        }

        if (typeof response.status === 'string') {
            const normalizedStatus = response.status.toLowerCase();
            return normalizedStatus.includes('disable') || normalizedStatus.includes('inactive');
        }

        return false;
    }

    private isLockedAccount(response: User): boolean {
        if (typeof this.optimisticIsLocked === 'boolean') {
            return this.optimisticIsLocked;
        }

        if (response.lockoutEndDate) {
            const lockoutEndDate = new Date(response.lockoutEndDate);
            if (!Number.isNaN(lockoutEndDate.getTime())) {
                return lockoutEndDate > new Date();
            }
        }

        if (typeof response.isLocked === 'boolean') {
            return response.isLocked;
        }

        return false;
    }

    private getAvatarText(response: User): string {
        const source = response.fullName || response.userName || '';
        const parts = source
            .split(/\s+/)
            .map((value) => value.trim())
            .filter((value) => value.length > 0);

        if (parts.length === 0) {
            return 'U';
        }

        if (parts.length === 1) {
            return parts[0].slice(0, 2).toUpperCase();
        }

        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }

    private getUserId(data: string | { id?: string }): string {
        if (typeof data === 'string') {
            return data;
        }

        if (data && typeof data.id === 'string') {
            return data.id;
        }

        return '';
    }

    private applyOptimisticUpdates(response: User): User {
        const mergedUser = Object.assign(new User(), response);

        mergedUser.status = this.optimisticStatus ?? response.status ?? null;
        mergedUser.statusChangedByUserId =
            response.statusChangedByUserId
            ?? this.optimisticStatusChangedByUserId
            ?? null;
        mergedUser.statusChangedByUserName =
            response.statusChangedByUserName
            ?? this.optimisticStatusChangedBy
            ?? null;
        mergedUser.statusChangedDate =
            response.statusChangedDate
            ?? this.optimisticStatusChangedAt
            ?? null;
        mergedUser.isLocked = this.optimisticIsLocked ?? response.isLocked;

        return mergedUser;
    }

    private disableUserAccount(userId: string): ReturnType<UserService['disableUserAccount']> {
        const targetStatus = this.getTargetStatus();
        if (targetStatus == null) {
            return this.userService.disableUserAccount(userId);
        }

        return this.userService.setUserAccountStatus(userId, targetStatus);
    }

    private enableUserAccount(userId: string): ReturnType<UserService['enableUserAccount']> {
        const targetStatus = this.getTargetStatus();
        if (targetStatus == null) {
            return this.userService.enableUserAccount(userId);
        }

        return this.userService.setUserAccountStatus(userId, targetStatus);
    }

    private getTargetStatus(): number | null {
        if (typeof this.currentUserStatus !== 'number') {
            return null;
        }

        if (this.currentUserStatus !== 0 && this.currentUserStatus !== 1) {
            return null;
        }

        return this.currentUserStatus === 0 ? 1 : 0;
    }

    private applyOptimisticAccountUpdate(action: AccountSecurityAction): void {
        const currentUser = this.authService.getCurrentUser();
        this.optimisticStatusChangedByUserId = currentUser?.userId || null;
        this.optimisticStatusChangedBy = currentUser?.userFullName || currentUser?.userEmail || null;
        this.optimisticStatusChangedAt = new Date().toISOString();

        if (action === 'disable' || action === 'enable') {
            const targetStatus = this.getTargetStatus();
            const fallbackStatus = action === 'disable' ? 1 : 0;
            const nextStatus = targetStatus ?? fallbackStatus;

            this.currentUserStatus = nextStatus;
            this.optimisticStatus = nextStatus;
        }

        if (action === 'unlock') {
            this.optimisticIsLocked = false;
        }
    }
}
